<template>
	<div class="edit-row edit-row--b-top edit-row--b-bottom">
		<div class="edit-row__label">{{ t('quickEditBar.visibilityFrom') }}</div>
		<field-select
			:label="t('sidebar.visibility.label.first').toLowerCase()"
			:label-inline="true"
			:label-classname="'mr--39'"
			:select-classname="'field--w-38'"
			:class="'mod--edit-bar mode--label-w-normal'"
			:options="zoomLevels"
			:options-available="zoomAvailableLevelsFirstLayer"
			v-model:selected="visibilityMin"
		></field-select>
		<field-select
			:label="t('sidebar.visibility.label.last').toLowerCase()"
			:label-inline="true"
			:label-classname="'mr--39'"
			:select-classname="'field--w-38'"
			:class="'mod--edit-bar mode--label-w-normal'"
			:options="zoomLevels"
			:options-available="zoomAvailableLevelsLastLayer"
			v-model:selected="visibilityMax"
		></field-select>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import FieldSelect from '@/components/fields/FieldSelect';
import MapUtils from '@/services/canvas/map-area/MapUtils';
import VisibilityUtils from '@/services/canvas/utils/VisibilityUtils';
import { TOASTS_TYPES } from '@/models/TOASTS_TYPES';
import ApiService from '@/utils/ApiService';
import ToastUtils from '@/utils/ToastUtils';

const { t } = useI18n();
const store = useStore();
const { createToastFromComponent } = ToastUtils();
const { getZoomLevelOptions } = MapUtils();
const { convertVisibilityToOriginalProperty } = VisibilityUtils();

const selectedMap = computed(() => store.getters['nodes/getSelectedNodes'] as Map<number, unknown>);
const layersAmount = computed(() => store.getters['layers/getLayersAmount'] as number);
const zoomLevels = computed(() => getZoomLevelOptions(layersAmount.value));

interface VisibilityNode {
	id: number;
	visibilityMin: number;
	visibilityMax: number;
}

const selectedNodes = computed((): VisibilityNode[] =>
	Array.from(selectedMap.value.values()) as VisibilityNode[]
);

const aggregatedMin = computed(() =>
	Math.max(...selectedNodes.value.map(n => n.visibilityMin))
);
const aggregatedMax = computed(() =>
	Math.min(...selectedNodes.value.map(n => n.visibilityMax))
);

const zoomAvailableLevelsFirstLayer = computed(() =>
	getZoomLevelOptions(Math.min(layersAmount.value, aggregatedMax.value), 1)
);
const zoomAvailableLevelsLastLayer = computed(() =>
	getZoomLevelOptions(layersAmount.value, aggregatedMin.value)
);

const visibilityMin = computed({
	get(): number { return aggregatedMin.value; },
	set(value: number) {
		applyVisibility(value, null);
	},
});

const visibilityMax = computed({
	get(): number { return aggregatedMax.value; },
	set(value: number) {
		applyVisibility(null, value);
	},
});

async function applyVisibility(newMin: number | null, newMax: number | null): Promise<void> {
	const nodesPayload: { id: number; visibility: Record<string, number> }[] = [];

	for (const node of selectedNodes.value) {
		const visMin = newMin ?? node.visibilityMin;
		const visMax = newMax ?? node.visibilityMax;
		const nodeData = {
			id: node.id,
			visibility: { visibilityMin: visMin, visibilityMax: visMax },
		};

		// Update local state + canvas
		store.dispatch('nodes/visibility/nodeVisibilityUpdate', nodeData);

		// Build batch payload with original property names
		const visibilityOriginal = {
			...convertVisibilityToOriginalProperty({ visibilityMin: visMin }),
			...convertVisibilityToOriginalProperty({ visibilityMax: visMax }),
		};
		nodesPayload.push({ id: node.id, visibility: visibilityOriginal });
	}

	// Single batch PATCH to elements endpoint
	const mapsURL = store.getters['maps/getMapsURL'];
	const elementsURL = store.getters['maps/getElementsURL'];
	const mapId = store.getters['maps/getCurrentMap'].id;

	try {
		await ApiService.patch(`${mapsURL}${mapId}${elementsURL}`, { nodes: nodesPayload });

		// Update raw nodes after successful save
		for (const node of selectedNodes.value) {
			const visMin = newMin ?? node.visibilityMin;
			const visMax = newMax ?? node.visibilityMax;
			store.dispatch('nodes/visibility/nodeOriginalVisibilityUpdate', {
				id: node.id,
				visibility: { visibilityMin: visMin, visibilityMax: visMax },
			});
		}
		await createToastFromComponent(TOASTS_TYPES.SUCCESS, t('message.nodeVisibilitySuccess', {id: ''}), store);
	} catch (err) {
		await createToastFromComponent(TOASTS_TYPES.ERROR, t('message.nodeVisibilityError', {id: ''}), store);
	}
}
</script>
